import { useEffect, useState } from 'react';

import type { QueryHookOptions, QueryResult } from '@main/core-ui';
import { buildUseQuery, logger } from '@main/core-ui';
import type {
  AnySchema,
  GqlObject,
  ParamsToType,
  QueryEndpoint,
  ResponseToType,
} from '@main/schema-utils';
import type { DecryptResponse } from '@main/sm-browser';
import type {
  DiffieHellmanParams,
  DiffieHellmanResponse,
} from '@main/sombra-types';
import { isDiffieHellmanError } from '@main/sombra-types';

import { useCheckSombraScopesError } from './useCheckSombraScopesError';
import { useDiffieHellman } from './useDiffieHellman';
import { useLogout } from './useLogout';

/**
 * A GraphQL query with diffie hellman support built in
 *
 * @param endpoint - The GraphQL query endpoint definition (requires dhEncrypted as input)
 * @param operationName - The GraphQL operation name
 * @param responseFields - Return a partial response
 * @returns A hook that calls useApolloQuery but adds type enforcements
 */
export function buildUseDhQuery<
  TName extends string,
  TParams extends DiffieHellmanParams,
  TResult extends DiffieHellmanResponse | AnySchema,
>(
  endpoint: QueryEndpoint<TName, TParams, TResult>,
  operationName?: string,
  responseFields?: GqlObject<TResult>,
  // TODO: https://github.com/transcend-io/main/issues/6322 - this response type should be partial if responseFields is provided
): (
  options?: QueryHookOptions<
    ResponseToType<TResult>,
    Omit<ParamsToType<TParams>, 'dhEncrypted'>
  >,
) => QueryResult<ResponseToType<TResult>> & {
  /**
   * A function that can be used to decrypt a DecryptionContext for that request.
   */
  decryptDhResponse: DecryptResponse;
} {
  // Create the raw hook using the core `buildUseQuery`
  const useQuery = buildUseQuery(endpoint, operationName, responseFields);

  // Wrap the hook with diffie hellman support
  return (options) => {
    const logout = useLogout();
    const [createDhEncrypted, decryptDhResponse] = useDiffieHellman();
    const checkSombraScopesError = useCheckSombraScopesError();
    // this is a bit weird, but we want to generate a dhEncrypted one time for the first query
    // we then create a new dhEncrypted on subsequent queries
    const [dhEncrypted] = useState(
      typeof createDhEncrypted === 'function' ? createDhEncrypted() : undefined,
    );

    /** The raw query */
    const { fetchMore, ...response } = useQuery({
      ...options,
      variables: {
        ...options?.variables,
        dhEncrypted,
      } as any,
    } as any);

    // Logout if session expires
    useEffect(() => {
      if (response.error?.message) {
        if (isDiffieHellmanError(response.error.message)) {
          logout();
        } else {
          checkSombraScopesError(response.error.message);
        }
      }
    }, [response.error]);

    if (!createDhEncrypted || !dhEncrypted) {
      // Log error if query was meant to be executed
      if (!options?.skip) {
        logger.error(
          'Failed to find diffie-hellman channel. The page should be logging out right now.',
        );
      }
      return {} as any;
    }

    return {
      ...response,
      fetchMore: ((fetchMoreOptions: any) =>
        fetchMore({
          ...fetchMoreOptions,
          variables: {
            // we refresh the dhEncrypted on each request
            dhEncrypted: createDhEncrypted(),
            ...fetchMoreOptions.variables,
          },
        })) as typeof fetchMore,
      decryptDhResponse,
    };
  };
}
